/**
 * Beschriftungen des Grundkalenders.
 *
 * Tagespreise laufen ueber `formatNightlyRate`: volle Euro ohne „,00", Cents nur wenn vorhanden
 * (PO-Entscheidung, FRONTEND_GUIDELINES §6).
 */
import { formatNightlyRate } from '@/shared/lib/format';
import { parseIso } from './calendarDates';

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

export function weekdayShort(iso: string): string {
  return WEEKDAYS[parseIso(iso).getUTCDay()] ?? '';
}

export function dayAndMonth(iso: string): string {
  const date = parseIso(iso);
  return `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}.`;
}

export function shortDate(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(parseIso(iso));
}

export function longDate(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parseIso(iso));
}

/** „14. – 20. September 2026“ — Monat und Jahr stehen nur, wo sie sich unterscheiden. */
export function windowLabel(from: string, to: string): string {
  const start = parseIso(from);
  const end = parseIso(to);
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  if (sameMonth && sameYear) {
    return `${start.getUTCDate()}. – ${longDate(to)}`;
  }
  if (sameYear) {
    return `${monthDay(from)} – ${longDate(to)}`;
  }
  return `${longDate(from)} – ${longDate(to)}`;
}

/** „16. – 17. Sep“ für die Zeitraum-Chips im Inline-Editor. */
export function rangeChipLabel(from: string, to: string): string {
  if (from === to) return shortDate(from);
  const sameMonth = parseIso(from).getUTCMonth() === parseIso(to).getUTCMonth();
  return sameMonth
    ? `${parseIso(from).getUTCDate()}. – ${shortDate(to)}`
    : `${shortDate(from)} – ${shortDate(to)}`;
}

export function nightsLabel(nights: number): string {
  return nights === 1 ? '1 Nacht' : `${nights} Nächte`;
}

export function rangesLabel(count: number): string {
  return count === 1 ? '1 Zeitraum' : `${count} Zeiträume`;
}

export function rate(value: number | null, currency: string): string {
  return value === null ? '—' : formatNightlyRate(value, currency);
}

function monthDay(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(parseIso(iso));
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
